import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import type { Day, POI, AccommodationConfig } from '@poi-planner/shared';
import { useTripStore } from '../store/tripStore';
import { useUIStore } from '../store/uiStore';

interface SleepIndicatorProps {
  dayIndex: number;
  day: Day;
  previousDay: Day | null;
  accommodationPOIs: POI[];
  accommodationConfigs: Record<string, AccommodationConfig>;
  getAccommodation: (id: string) => POI | undefined;
  nightCount: { current: number; total: number } | null;
}

export function SleepIndicator({
  day,
  previousDay,
  accommodationPOIs,
  accommodationConfigs,
  getAccommodation,
  nightCount,
}: SleepIndicatorProps) {
  const setSleep = useTripStore((s) => s.setSleep);
  const setAccommodationConfig = useTripStore((s) => s.setAccommodationConfig);
  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const openAccomConfigEditor = useUIStore((s) => s.openEditor);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const accommodation = day.sleep ? getAccommodation(day.sleep.poiId) : null;
  const accomName = accommodation?.name ?? 'No accommodation set';
  const isSwap = previousDay?.sleep && day.sleep &&
    previousDay.sleep.poiId !== day.sleep.poiId;

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const handleSelect = (poiId: string | null) => {
    setSleep(day.id, poiId);
    setDropdownOpen(false);
  };

  const handleCreateNew = () => {
    setDropdownOpen(false);
    // Open POI editor with "accommodation" tag pre-set
    openPOIEditor('create');
    // Note: The POI editor should add the accommodation tag automatically
    // We'll update the POI editor to support this
  };

  const handleConfigureAccom = (e: React.MouseEvent, poiId: string) => {
    e.stopPropagation();
    setDropdownOpen(false);
    // Open accommodation config editor
    openAccomConfigEditor({
      type: 'accommodationConfig',
      mode: 'edit',
      entityId: poiId,
      context: { dayId: day.id },
    });
  };

  return (
    <div
      ref={dropdownRef}
      className="p-3 border-t border-border bg-white/60 relative"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-[10px] font-bold text-text-secondary uppercase tracking-widest">
          <span className="material-symbols-outlined text-sm">bedtime</span>
          <span>Where to Sleep</span>
        </div>

        {day.sleep ? (
          <div
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex flex-col gap-1 p-3 bg-[var(--accom-sleep-bg)] border border-[var(--accom-sleep-border)] rounded-xl cursor-pointer transition-shadow hover:shadow-md"
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-bold text-[var(--accom-sleep-text)]">
                  {accomName}
                </span>
                <span className="material-symbols-outlined text-base text-[var(--accom-sleep-text)] opacity-50">
                  unfold_more
                </span>
              </div>
              {nightCount && (
                <div className="flex items-center bg-[var(--accom-badge-bg)] px-1.5 py-0.5 rounded text-[10px] font-bold text-[var(--accom-badge-text)] whitespace-nowrap">
                  Night {nightCount.current}/{nightCount.total}
                </div>
              )}
            </div>
            {isSwap && (
              <span className="text-[10px] font-medium text-[var(--accom-text)]">
                Hotel Swap Complete
              </span>
            )}
          </div>
        ) : (
          <div
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="p-3 bg-input-bg rounded-xl text-[13px] text-text-muted cursor-pointer flex items-center justify-between transition-shadow hover:shadow-md"
          >
            <span>No accommodation assigned</span>
            <span className="material-symbols-outlined text-base opacity-50">unfold_more</span>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {dropdownOpen && (
        <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-border rounded-xl shadow-lg z-50 overflow-hidden max-h-60 overflow-y-auto">
          {/* None option */}
          <button
            onClick={() => handleSelect(null)}
            className={clsx(
              'w-full px-3.5 py-2.5 flex items-center gap-2 border-none border-b border-border cursor-pointer text-[13px] font-sans text-text-muted text-left transition-colors hover:bg-input-bg',
              !day.sleep && 'bg-input-bg'
            )}
          >
            <span className="material-symbols-outlined text-lg">block</span>
            <span>None</span>
          </button>

          {/* Accommodation options - POIs with "accommodation" tag */}
          {accommodationPOIs.map((poi) => {
            const isSelected = day.sleep?.poiId === poi.id;
            const config = accommodationConfigs[poi.id];
            return (
              <button
                key={poi.id}
                onClick={() => handleSelect(poi.id)}
                className={clsx(
                  'w-full px-3.5 py-2.5 flex items-center gap-2 border-none border-b border-border cursor-pointer text-[13px] font-sans text-text-primary text-left transition-colors hover:bg-input-bg',
                  isSelected && 'bg-input-bg font-semibold'
                )}
              >
                <span className="material-symbols-outlined text-lg text-text-secondary">hotel</span>
                <span className="flex-1">{poi.name}</span>
                {isSelected && (
                  <span className="material-symbols-outlined text-lg text-green-500">check</span>
                )}
                {config && (
                  <span
                    className="material-symbols-outlined text-base text-text-muted p-0.5 rounded transition-colors hover:text-text-primary"
                    onClick={(e) => handleConfigureAccom(e, poi.id)}
                    title="Configure color & times"
                  >
                    tune
                  </span>
                )}
              </button>
            );
          })}

          {/* Create new */}
          <button
            onClick={handleCreateNew}
            className="w-full px-3.5 py-2.5 flex items-center gap-2 bg-transparent border-none cursor-pointer text-[13px] font-semibold font-sans text-sleep-accent text-left transition-colors hover:bg-input-bg"
          >
            <span className="material-symbols-outlined text-lg">add_circle</span>
            <span>Create new accommodation...</span>
          </button>
        </div>
      )}
    </div>
  );
}
